import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { History } from 'lucide-react';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import FiscalTabs from '../../../components/FiscalEvents/FiscalTabs';
import ReturnDocumentSelector from '../../../components/FiscalEvents/ReturnDocumentSelector';
import ReturnEventWizard from '../../../components/FiscalEvents/ReturnEventWizard';
import FiscalEventService from '../../../services/fiscal/FiscalEventService';
import { FiscalEvent } from '../../../types/fiscal/FiscalEvent';
import { ReturnDocumentCandidate } from '../../../types/fiscal/ReturnEvent';
import FiscalEventsList from '../../../components/FiscalEvents/FiscalEventsList';
import FiscalEventDetailModal from '../../../components/FiscalEvents/FiscalEventDetailModal';
import useAlerts from '../../../hooks/useAlerts';

export default function ReturnEventsPage() {
  const { saleId } = useParams<{ saleId?: string }>();
  const navigate = useNavigate();
  const parsedSaleId = saleId ? Number(saleId) : undefined;

  const [tab, setTab] = useState(0);
  const [selectedDocument, setSelectedDocument] =
    useState<ReturnDocumentCandidate>();
  const [events, setEvents] = useState<FiscalEvent[]>([]);
  const [detail, setDetail] = useState<FiscalEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const { show } = useAlerts();

  const searchEvents = async (candidate: ReturnDocumentCandidate) => {
    setSelectedDocument(candidate);
    setLoading(true);
    try {
      setEvents(
        (await FiscalEventService.findBySale(candidate.saleId)).filter(
          (event) => event.eventType === 'RETURN'
        )
      );
    } catch (error: any) {
      show(
        'error',
        error?.response?.data?.title || 'Consulta fallida',
        error?.response?.data?.message ||
          'No se pudieron consultar los eventos.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <main className="space-y-5 rounded-xl border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark sm:p-6">
        <header>
          <h1 className="text-xl font-semibold text-black dark:text-white">
            Eventos de Retorno
          </h1>
          <p className="mt-1 text-sm text-body">
            Seleccione documentos fiscales; la elegibilidad, los saldos y las
            reglas se validan en el backend.
          </p>
        </header>
        <FiscalTabs
          ariaLabel="Secciones de eventos de retorno"
          tabs={[
            { label: 'Nuevo Retorno' },
            { label: 'Eventos generados', count: events.length },
          ]}
          activeIndex={tab}
          onChange={setTab}
        />
        {tab === 0 ? (
          <ReturnEventWizard
            embedded
            isOpen
            initialSaleId={parsedSaleId}
            onClose={() => {
              if (parsedSaleId) navigate('/sales/returns');
            }}
            onCreated={() => {
              setTab(1);
              if (parsedSaleId) navigate('/sales/returns');
            }}
          />
        ) : (
          <section className="space-y-5">
            <div className="rounded-xl border border-stroke p-4 dark:border-strokedark">
              <div className="mb-4 flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-black dark:text-white">
                  Consultar eventos por documento
                </h2>
              </div>
              <ReturnDocumentSelector
                mode="single"
                selected={selectedDocument ? [selectedDocument] : []}
                onSelect={searchEvents}
                disabled={loading}
              />
            </div>
            {selectedDocument && (
              <div className="rounded-xl bg-gray-2 p-4 text-sm dark:bg-meta-4">
                <span className="font-medium text-black dark:text-white">
                  Documento: {selectedDocument.documentNumber}
                </span>
                <span className="ml-3 text-body">
                  {selectedDocument.generationCode}
                </span>
              </div>
            )}
            <FiscalEventsList events={events} onOpen={setDetail} />
          </section>
        )}
      </main>
      <FiscalEventDetailModal event={detail} onClose={() => setDetail(null)} />
    </WorkspaceLayout>
  );
}
